// views/Ticketing/TicketOrderDetails.js
// ─────────────────────────────────────────────────────────────────────────────
// One ticket order, from reservation to the gate. Read only (16 Oct 2026).
//
// Ticket orders were easy to miss and impossible to follow from here. They
// never counted as new orders, and the order page drew them as a delivery:
// every rung of "Processing, Assigned, Dispatched, Delivered" ticked, the
// walk-in banner "Fulfilled at the counter", the line still PROCESSING after
// its ticket was issued, rider and dispatch controls. None of it said whether
// the buyer had the ticket, opened it, or got in.
//
// This page draws the ticket's own cycle (Reserved, Paid, Tickets issued, With
// the buyer, Admitted), each ticket and its state, every scan at the gate
// (refusals too, with why), refunds, what the payment provider said, and a
// timeline. Everything comes from GET /api/ticketing/orders/:id/lifecycle
// (server: services/ticketing/ticketOrderLifecycle.js); this file only draws.
//
// Every route to a ticket order lands here: the orders table, the generic
// order page (it redirects), the dashboard card, and the new-sale alert.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Container } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";
import moment from "moment";

import { apiGetTicketOrderLifecycle } from "../../api";
import Loading from "../../components/Loading";
import StepLadder from "../../components/Orders/StepLadder";
import { Fact, Panel } from "../../components/Orders/OrderPanel";
import OrderProviderCalls from "../../components/payments/OrderProviderCalls";
import { StatusBadge, TicketStageBadge, TICKET_STAGE_CFG } from "../../components/OrderStatusBadges";
import { formatMoney } from "../../utilities/orderSummary";
import { getUserFromLocalStorage } from "../../utilities/user.js";
import "../../components/Orders/orderDetailsTheme.css";

const REFRESH_MS = 30 * 1000;

const when = (iso) => (iso ? moment(iso).format("MMM D, h:mm a") : null);
const whenLong = (iso) => (iso ? moment(iso).format("ddd D MMM YYYY, h:mm a") : null);
const money = (amount, currency) => formatMoney(amount, currency === "UGX" ? "Ush" : currency || "Ush");

const TICKET_STATE = Object.freeze({
  VALID: { label: "Valid", tone: "info" },
  ADMITTED: { label: "Admitted", tone: "success" },
  REFUNDED: { label: "Refunded", tone: "neutral" },
  VOID: { label: "Void", tone: "danger" },
});

// What a person should know when the order needs one.
const ATTENTION_NOTE = Object.freeze({
  NOT_ISSUED:
    "Paid, but no tickets were issued. The server tries again every minute on its own. If this stays, check the reservation and what the payment provider said below.",
  REFUND_REQUIRED:
    "Paid, but the seats were taken before the tickets could be issued. The buyer is owed a refund.",
});

// The reservation, in plain words (TicketHold.status).
const HOLD_STATUS = Object.freeze({
  ACTIVE: "held",
  CONVERTED: "turned into tickets",
  EXPIRED: "expired",
  RELEASED: "released",
});

const TONE_DOT = Object.freeze({
  success: "#16a34a",
  danger: "#dc2626",
  warning: "#d97706",
  neutral: "#94a3b8",
  info: "#0284c7",
});

const TicketOrderDetails = () => {
  const { id } = useParams();
  const history = useHistory();
  const user = getUserFromLocalStorage();
  const isStaff = user?.result?.userType === "LUSUKU STAFF" || user?.result?.user_role === "SUPERUSER";

  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  // Whether anything is on screen: a failed quiet refresh must not blank it.
  const shownRef = useRef(false);

  const load = useCallback(
    async (quiet = false) => {
      if (quiet) setRefreshing(true);
      try {
        const { data: body } = await apiGetTicketOrderLifecycle(id);
        setData(body);
        shownRef.current = true;
        setError("");
      } catch (err) {
        const status = err?.response?.status;
        // A quiet refresh that fails keeps what is on screen.
        if (!quiet || !shownRef.current) {
          setError(
            status === 404
              ? "This is not a ticket order, or it no longer exists."
              : status === 403
                ? "You are not allowed to view this order."
                : err?.response?.data?.message || "Could not load this ticket order."
          );
        }
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [id]
  );

  useEffect(() => {
    setLoading(true);
    load(false);
  }, [load]);

  // Gate scans and payments land while the page is open: a quiet re-read
  // while the tab is visible.
  useEffect(() => {
    const tick = setInterval(() => {
      if (document.visibilityState === "visible") load(true);
    }, REFRESH_MS);
    return () => clearInterval(tick);
  }, [load]);

  if (loading) {
    return (
      <Container fluid>
        <Loading size="page" label="Loading this ticket order..." />
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid>
        <div className="rod-canvas">
          <div className="lk-note lk-note--danger" style={{ marginBottom: 14 }}>
            <span>{error}</span>
          </div>
          <button type="button" className="lk-action-btn lk-action-btn--lg" onClick={() => history.goBack()}>
            Back
          </button>
        </div>
      </Container>
    );
  }

  const { stage, steps = [], buyer = {}, event = {}, payment = {}, reservation, tickets = [], scans = [], refunds = [], timeline = [], totals = {} } = data;
  const ref = data.orderNumber || String(data.orderId).slice(-8).toUpperCase();
  const ladder = steps.map((s) => ({
    key: s.key,
    label: s.label,
    state: s.state,
    sub: (s.at || s.detail) && (
      <>
        {when(s.at)}
        {s.at && s.detail && <br />}
        {s.detail}
      </>
    ),
  }));

  return (
    <Container fluid>
      <div className="rod-canvas">
        {/* ── Header ───────────────────────────────────────────────────── */}
        <div className="rod-topbar">
          <div className="rod-topbar-title">
            <h4>{buyer.name || "Ticket buyer"}</h4>
            <span>
              Ticket order #{ref}
              {data.createdAt && ` · ${moment(data.createdAt).format("MMM D, YYYY · h:mm a")}`}
            </span>
          </div>
          <div className="rod-topbar-actions" style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <TicketStageBadge stage={stage} />
            {data.salesChannel === "BOX_OFFICE" && (
              <StatusBadge label="Box office" cfg={TICKET_STAGE_CFG.neutral} />
            )}
            <button
              type="button"
              className="lk-action-btn lk-action-btn--lg"
              onClick={() => load(true)}
              disabled={refreshing}
            >
              {refreshing ? "Refreshing..." : "Refresh"}
            </button>
            <button type="button" className="lk-action-btn lk-action-btn--lg lk-action-btn--quiet" onClick={() => history.goBack()}>
              Back
            </button>
          </div>
        </div>

        {stage?.attention && (
          <div className="lk-note lk-note--danger" style={{ marginBottom: 14 }}>
            <span>
              <strong>Needs attention.</strong> {ATTENTION_NOTE[stage.key] || stage.label}
            </span>
          </div>
        )}

        <StepLadder steps={ladder} label="Ticket order progress" />

        <div className="rod-main-grid">
          {/* ── Event ───────────────────────────────────────────────── */}
          <Panel title="Event">
            <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
              {event.posterUrl && (
                <img
                  src={event.posterUrl}
                  alt=""
                  style={{ width: 72, height: 96, objectFit: "cover", borderRadius: 6, flexShrink: 0, background: "#f1f5f9" }}
                />
              )}
              <div className="rod-facts" style={{ flex: 1 }}>
                <Fact label="Event" value={event.title} wide />
                <Fact label="Showing" value={whenLong(event.startsAt)} />
                <Fact label="Doors" value={when(event.doorsAt)} />
                <Fact label="Venue" value={event.venue} />
                <Fact label="Sold by" value={event.organizer} />
                <Fact
                  label="Showing status"
                  value={event.showingStatus && event.showingStatus !== "PUBLISHED" ? event.showingStatus : null}
                />
                <Fact
                  label="Reservation"
                  wide
                  value={
                    reservation
                      ? [
                          reservation.held,
                          reservation.status === "ACTIVE" && reservation.expiresAt
                            ? `held until ${when(reservation.expiresAt)}`
                            : HOLD_STATUS[reservation.status] || reservation.status,
                        ]
                          .filter(Boolean)
                          .join(" · ")
                      : null
                  }
                />
              </div>
            </div>
          </Panel>

          {/* ── Buyer and payment ───────────────────────────────────── */}
          <Panel title="Buyer and payment">
            <div className="rod-facts">
              <Fact label="Buyer" value={buyer.name} />
              <Fact label="Phone" value={buyer.phone} />
              <Fact label="Email" value={buyer.email} />
              <Fact label="Method" value={payment.method} />
              <Fact label={payment.paid ? "Total charged" : "Order total"} value={money(payment.total, payment.currency)} />
              <Fact label="Paid on" value={payment.paid ? whenLong(payment.paidAt) || "Paid" : payment.failed ? "Payment failed" : "Not paid"} />
              <Fact label="Payment reference" value={payment.transactionId} />
              <Fact label="VAT included" value={payment.tax ? money(payment.tax, payment.currency) : null} />
              {/* What the sale earns: only once there is a sale. */}
              <Fact
                label="Lusuku commission"
                value={
                  payment.paid && payment.commission
                    ? `${money(payment.commission.amount, payment.currency)}${payment.commission.rate != null ? ` (${payment.commission.rate}%)` : ""}`
                    : null
                }
              />
              <Fact
                label="Organizer receives"
                value={payment.paid && payment.commission ? money(payment.commission.organizerNet, payment.currency) : null}
              />
            </div>
          </Panel>
        </div>

        {/* ── Tickets ──────────────────────────────────────────────────── */}
        <Panel
          title={`Tickets (${tickets.length})`}
          style={{ marginBottom: 14 }}
          action={
            tickets.length ? (
              <span className="rod-hint">
                {totals.admitted} of {totals.tickets} admitted · {totals.live} opened live on a phone
              </span>
            ) : null
          }
        >
          {tickets.length ? (
            <div className="rod-table-wrap">
              <table className="rod-data-table rod-data-table--left">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Serial</th>
                    <th>Ticket</th>
                    <th>Seat</th>
                    <th>Admits</th>
                    <th style={{ textAlign: "right" }}>Price</th>
                    <th>Status</th>
                    <th>On a phone</th>
                    <th>At the gate</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((t, i) => {
                    const st = TICKET_STATE[t.state] || TICKET_STATE.VALID;
                    return (
                      <tr key={t.id}>
                        <td>{i + 1}</td>
                        <td style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontWeight: 600 }}>{t.serial || "No serial"}</td>
                        <td>{t.type || "Ticket"}</td>
                        <td>{t.seat || "General"}</td>
                        <td>{t.admits}</td>
                        <td style={{ textAlign: "right" }}>{money(t.price, payment.currency)}</td>
                        <td>
                          <StatusBadge label={st.label} cfg={TICKET_STAGE_CFG[st.tone]} />
                        </td>
                        <td>
                          {t.liveSince
                            ? `Since ${when(t.liveSince)}${t.liveMoves ? ` · moved ${t.liveMoves}x` : ""}`
                            : "Not opened live"}
                        </td>
                        <td>
                          {t.admittedAt
                            ? `${when(t.admittedAt)}${t.gate ? ` · ${t.gate}` : ""}`
                            : t.voidedAt
                              ? `Voided ${when(t.voidedAt)}${t.voidReason ? ` · ${t.voidReason}` : ""}`
                              : "Not yet"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="rod-hint">No tickets have been issued for this order.</div>
          )}
        </Panel>

        {/* ── At the gate ──────────────────────────────────────────────── */}
        <Panel
          title={`At the gate (${scans.length})`}
          style={{ marginBottom: 14 }}
          action={totals.refusals ? <span className="rod-hint rod-hint-warn">{totals.refusals} refused</span> : null}
        >
          {scans.length ? (
            <div className="rod-table-wrap">
              <table className="rod-data-table rod-data-table--left">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Ticket</th>
                    <th>Result</th>
                    <th>What was shown</th>
                    <th>Gate</th>
                    <th>Read by</th>
                  </tr>
                </thead>
                <tbody>
                  {scans.map((s, i) => (
                    <tr key={`${s.at}-${i}`}>
                      <td style={{ whiteSpace: "nowrap" }}>{when(s.at)}</td>
                      <td style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>{s.serial || "Unknown"}</td>
                      <td>
                        <StatusBadge label={s.result} cfg={TICKET_STAGE_CFG[s.admitted ? "success" : "danger"]} />
                      </td>
                      <td>{s.reason || (s.method === "SERIAL" ? "The serial, typed in" : "The QR")}</td>
                      <td>{[s.gate, s.terminalId].filter(Boolean).join(" · ") || "Not recorded"}</td>
                      <td>{s.method === "SERIAL" ? "Typed serial" : "QR scan"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="rod-hint">No scans yet. Every scan of these tickets, admitted or refused, shows here.</div>
          )}
        </Panel>

        {refunds.length > 0 && (
          <Panel title={`Refunds (${refunds.length})`} style={{ marginBottom: 14 }}>
            <div className="rod-table-wrap">
              <table className="rod-data-table rod-data-table--left">
                <thead>
                  <tr>
                    <th>Requested</th>
                    <th>Status</th>
                    <th style={{ textAlign: "right" }}>Amount</th>
                    <th>Reason</th>
                    <th>Decided</th>
                    <th>Paid out</th>
                  </tr>
                </thead>
                <tbody>
                  {refunds.map((r) => (
                    <tr key={r.id}>
                      <td>{when(r.requestedAt)}</td>
                      <td>{r.status}</td>
                      <td style={{ textAlign: "right" }}>{money(r.amount, payment.currency)}</td>
                      <td>{r.reason || "None given"}</td>
                      <td>{when(r.decidedAt) || "Pending"}</td>
                      <td>{r.processedAt ? `${when(r.processedAt)}${r.reference ? ` · ${r.reference}` : ""}` : "Not yet"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        )}

        <div className="rod-main-grid">
          {/* ── Timeline ─────────────────────────────────────────────── */}
          <Panel title="Timeline">
            {timeline.length ? (
              <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {timeline.map((e, i) => (
                  <li key={`${e.at}-${i}`} style={{ display: "flex", gap: 10, padding: "6px 0", borderBottom: i < timeline.length - 1 ? "1px solid #f1f5f9" : "none" }}>
                    <span
                      aria-hidden="true"
                      style={{ width: 8, height: 8, borderRadius: "50%", marginTop: 6, flexShrink: 0, background: TONE_DOT[e.tone] || TONE_DOT.neutral }}
                    />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{e.title}</div>
                      {e.detail && <div className="rod-hint" style={{ overflowWrap: "anywhere" }}>{e.detail}</div>}
                    </div>
                    <div className="rod-hint" style={{ marginLeft: "auto", whiteSpace: "nowrap" }}>{when(e.at)}</div>
                  </li>
                ))}
              </ol>
            ) : (
              <div className="rod-hint">Nothing recorded yet.</div>
            )}
          </Panel>

          {/* ── What the provider said (staff) ───────────────────────── */}
          {isStaff ? (
            <div className="rod-panel">
              <div className="rod-panel-body">
                <OrderProviderCalls orderId={data.orderId} />
              </div>
            </div>
          ) : (
            <div />
          )}
        </div>
      </div>
    </Container>
  );
};

export default TicketOrderDetails;
